import { CSSProperties, MouseEventHandler, useCallback, useLayoutEffect, useRef, useState } from 'react';

const NOTICE_GAP_PX = 32;
const NOTICE_SPEED_PX_PER_SECOND = 40;
const MIN_DURATION_SECONDS = 8;
const MAX_DURATION_SECONDS = 90;
const OVERFLOW_TOLERANCE_PX = 1;

type NoticeStyle = CSSProperties & {
  '--notice-translate-x': string;
  '--notice-duration': string;
};

interface AutoScrollNoticeProps {
  text: string;
  className?: string;
  ariaLabel?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

interface ScrollMetrics {
  overflowing: boolean;
  distance: number;
  duration: number;
}

export function calculateNoticeDuration(distance: number) {
  return Math.min(
    MAX_DURATION_SECONDS,
    Math.max(MIN_DURATION_SECONDS, distance / NOTICE_SPEED_PX_PER_SECOND),
  );
}

function readHorizontalPadding(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  return (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0);
}

export default function AutoScrollNotice({
  text,
  className = '',
  ariaLabel = '落地页提示词',
  onClick,
}: AutoScrollNoticeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  const [metrics, setMetrics] = useState<ScrollMetrics>({
    overflowing: false,
    distance: 0,
    duration: MIN_DURATION_SECONDS,
  });

  const measure = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    if (!text) {
      setMetrics({ overflowing: false, distance: 0, duration: MIN_DURATION_SECONDS });
      return;
    }

    const availableWidth = Math.max(0, container.clientWidth - readHorizontalPadding(container));
    const contentWidth = Math.max(content.scrollWidth, content.getBoundingClientRect().width);
    const overflowing = contentWidth > availableWidth + OVERFLOW_TOLERANCE_PX;
    const distance = overflowing ? contentWidth + NOTICE_GAP_PX : 0;
    const duration = calculateNoticeDuration(distance);

    setMetrics((previous) => {
      if (
        previous.overflowing === overflowing &&
        previous.distance === distance &&
        previous.duration === duration
      ) {
        return previous;
      }
      return { overflowing, distance, duration };
    });
  }, [text]);

  useLayoutEffect(() => {
    measure();

    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return undefined;

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(container);
      observer.observe(content);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const noticeStyle: NoticeStyle | undefined = metrics.overflowing
    ? {
        '--notice-translate-x': `-${metrics.distance}px`,
        '--notice-duration': `${metrics.duration}s`,
      }
    : undefined;

  return (
    <div
      ref={containerRef}
      className={`${className} ${metrics.overflowing ? 'tips-block--scrolling' : ''}`.trim()}
      role="note"
      aria-label={ariaLabel}
      onClick={onClick}
      style={noticeStyle}
    >
      <div className="tips-block__track">
        <span ref={contentRef} className="tips-block__content">
          {text}
        </span>
        {metrics.overflowing && (
          <span className="tips-block__content tips-block__content--copy" aria-hidden="true">
            {text}
          </span>
        )}
      </div>
    </div>
  );
}
